import { useEffect } from 'react';
// Hash routing, not browser routing: a Tauri desktop build loads from file://,
// where path-based routes 404. Choosing this on day one is what makes the .exe
// a packaging step later rather than a rewrite. See CLAUDE.md.
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { isAdmin } from '@vns/core';
import { useSession } from './lib/session';
import { applyTheme, resolveInitialTheme } from './lib/theme';
import { ToastProvider } from './components/ui';
import Login from './routes/Login';
import Dashboard from './routes/Dashboard';
import Activity from './routes/Activity';
import Clients from './routes/Clients';
import ClientDetail from './routes/ClientDetail';
import Projects from './routes/Projects';
import ProjectDetail from './routes/ProjectDetail';
import Suppliers from './routes/Suppliers';
import SupplierDetail from './routes/SupplierDetail';
import Subcontractors from './routes/Subcontractors';
import SubcontractorDetail from './routes/SubcontractorDetail';
import Materials from './routes/Materials';
import MaterialDetail from './routes/MaterialDetail';
import Team from './routes/Team';
import MyEntries from './routes/MyEntries';
import Approvals from './routes/Approvals';
import BankAccounts from './routes/BankAccounts';
import FundTransfers from './routes/FundTransfers';
import StockTransfers from './routes/StockTransfers';
import Reports from './routes/Reports';
import ReportView from './routes/ReportView';
import ProjectReport from './routes/ProjectReport';
import MaterialReport from './routes/MaterialReport';
import DailyReport from './routes/DailyReport';
import Notifications from './routes/Notifications';
import Settings from './routes/Settings';
import ProjectDrawings from './routes/ProjectDrawings';
import Transactions from './routes/Transactions';
import PaymentReminder from './components/PaymentReminder';
import { RealtimeSync } from './lib/useRealtimeSync';

/** Blocks a staff member from an admin-only route even by typing the URL. */
function AdminOnly({ children }: { children: React.ReactNode }) {
  const actor = useSession((s) => s.actor);
  return isAdmin(actor) ? <>{children}</> : <Navigate to="/" replace />;
}

export default function App() {
  const { actor, loading, init } = useSession();

  useEffect(() => { void init(); }, [init]);

  useEffect(() => {
    if (!loading) applyTheme();
  }, [loading]);

  if (loading) return <div className="center-note">Loading…</div>;

  return (
    <ToastProvider>
      {actor && <RealtimeSync />}
      <HashRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        {actor && <PaymentReminder />}
        <Routes>
          <Route path="/login" element={actor ? <Navigate to="/" replace /> : <Login />} />
          {actor ? (
            <>
              <Route path="/" element={<Dashboard />} />
              <Route path="/activity" element={<AdminOnly><Activity /></AdminOnly>} />
              <Route path="/clients" element={<AdminOnly><Clients /></AdminOnly>} />
              <Route path="/clients/:id" element={<AdminOnly><ClientDetail /></AdminOnly>} />
              <Route path="/projects" element={<Navigate to="/clients" replace />} />
              <Route path="/projects/:id" element={<ProjectDetail />} />
              <Route path="/project-drawings" element={<ProjectDrawings />} />
              <Route path="/transactions" element={<AdminOnly><Transactions /></AdminOnly>} />
              <Route path="/transactions/:type" element={<AdminOnly><Transactions /></AdminOnly>} />
              <Route path="/suppliers" element={<AdminOnly><Suppliers /></AdminOnly>} />
              <Route path="/suppliers/:id" element={<AdminOnly><SupplierDetail /></AdminOnly>} />
              <Route path="/subcontractors" element={<AdminOnly><Subcontractors /></AdminOnly>} />
              <Route path="/subcontractors/:id" element={<AdminOnly><SubcontractorDetail /></AdminOnly>} />
              <Route path="/materials" element={<AdminOnly><Materials /></AdminOnly>} />
              <Route path="/materials/:id" element={<AdminOnly><MaterialDetail /></AdminOnly>} />
              <Route path="/team" element={<AdminOnly><Team /></AdminOnly>} />
              <Route path="/my-entries" element={<MyEntries />} />
              {/* Staff see this too — it is where a returned entry explains itself. */}
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/approvals" element={<AdminOnly><Approvals /></AdminOnly>} />
              <Route path="/bank-accounts" element={<AdminOnly><BankAccounts /></AdminOnly>} />
              <Route path="/fund-transfers" element={<AdminOnly><FundTransfers /></AdminOnly>} />
              <Route path="/stock-transfers" element={<StockTransfers />} />
              <Route path="/settings" element={<AdminOnly><Settings /></AdminOnly>} />
              <Route path="/reports" element={<AdminOnly><Reports /></AdminOnly>} />
              {/* Static paths, so they outrank the dynamic :key route below
                  regardless of declaration order — React Router matches the
                  more specific segment first. */}
              <Route path="/reports/project" element={<AdminOnly><ProjectReport /></AdminOnly>} />
              <Route path="/reports/material" element={<AdminOnly><MaterialReport /></AdminOnly>} />
              <Route path="/reports/daily" element={<AdminOnly><DailyReport /></AdminOnly>} />
              <Route path="/reports/:key" element={<AdminOnly><ReportView /></AdminOnly>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          ) : (
            <Route path="*" element={<Navigate to="/login" replace />} />
          )}
        </Routes>
      </HashRouter>
    </ToastProvider>
  );
}
